import { useEffect, useRef, useState } from "react";
import DropdownActivityContent from "./DropdownActivityContent";
import FillInTheBlanksActivityContent from "./FillInTheBlanksActivityContent";
import GroupingActivityContent from "./GroupingActivityContent";
import MatchingActivityContent from "./MatchingActivityContent";
import MCMAActivityContent from "./MCMAActivityContent";
import MCSAActivityContent from "./MCSAActivityContent";
import OpenEndedActivityContent from "./OpenEndedActivityContent";
import OrderingActivityContent from "./OrderingActivityContent";
import SelectionBox from "../boxes/SelectionBox";
import TrueFalseActivityContent from "./TrueFalseActivityContent";
import ActivitySolutionContent from "./solution-contents/ActivitySolutionContent";
import ActivityEvaluationRubricContent from "./evaluation-rubric-contents/ActivityEvaluationRubricContent";
import ActivityBodyContent from "./body-contents/ActivityBodyContent";
import PassageReferenceBlock from "./PassageReferenceBlock";
import DividerLine from "../dividers/DividerLine";
import i18n from "../../language/i18n";
import {
  constructActivityAnswerMap,
  readActivityDataField,
  retrieveDifficultyByActivityTypeFromData,
} from "../../utilization/CatchtivityUtilization";
import { IActivityPreviewByDataProps } from "../../properties/ActivityProperties";
import ActivityEmptyContent from "./empty-contents/ActivityEmptyContent";

const ACTIVITY_TEMPLATE_LIST = [
  { type: "ORDERING", materialMap: "orderingMaterialMap" },
  { type: "DROPDOWN", materialMap: "dropdownMaterialMap" },
  { type: "MCSA", materialMap: "MCSAMaterialMap" },
  { type: "MCMA", materialMap: "MCMAMaterialMap" },
  { type: "MATCHING", materialMap: "matchingMaterialMap" },
  { type: "GROUPING", materialMap: "groupingMaterialMap" },
  {
    type: "FILL_IN_THE_BLANKS",
    materialMap: "fillInTheBlanksMaterialMap",
  },
  { type: "OPEN_ENDED", materialMap: "openEndedMaterialMap" },
  { type: "TRUE_FALSE", materialMap: "trueFalseMaterialMap" },
];

const ActivityPreviewByData = ({
  data,
  changeAnswer = () => {},
  answerType = null,
  showType = true,
  showDescription = true,
  typeList = [],
  showSolution = false,
  showEvaluationRubric = false,
  showDifficulty = true,
  isFullScreen = false,
  showCorrectAnswer = false,
  showMaterialContent = true,
  isPreview = true,
  passageBodyMap,
  passageReference,
}: IActivityPreviewByDataProps) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [optionList, setOptionList] = useState<any[]>([]);
  const [answerMapMap, setAnswerMapMap] = useState<any>({});
  const [activityTemplateTypeList, setActivityTemplateTypeList] = useState<
    any[]
  >([]);

  useEffect(() => {
    if (!data) return;
    let currentActivityTemplateMapList: any[] = JSON.parse(
      JSON.stringify(ACTIVITY_TEMPLATE_LIST)
    );
    if (typeList.length > 0) {
      currentActivityTemplateMapList = currentActivityTemplateMapList.filter(
        (activityTemplateMap) => typeList.includes(activityTemplateMap.type)
      );
    }
    setActivityTemplateTypeList(currentActivityTemplateMapList);
  }, [data, answerType, typeList]);

  useEffect(() => {
    if (activityTemplateTypeList.length === 0) return;
    const currentOptionList = [];
    for (const activityTemplateType of activityTemplateTypeList) {
      const currentTypeOption: any = {
        id: activityTemplateType.type,
        text: i18n.t(activityTemplateType.type),
      };
      if (showDifficulty) {
        currentTypeOption.subText = i18n.t(
          retrieveDifficultyByActivityTypeFromData(
            activityTemplateType.type,
            data
          )
        );
      }
      currentOptionList.push(currentTypeOption);
    }
    setOptionList(currentOptionList);
  }, [activityTemplateTypeList, showDifficulty]);

  useEffect(() => {
    if (optionList.length === 0) return;
    setSelectedType(optionList[0].id);
  }, [optionList]);

  const prevDataStringRef = useRef<string>("");
  useEffect(() => {
    if (activityTemplateTypeList.length === 0) return;
    const dataString = JSON.stringify(data);
    if (dataString === prevDataStringRef.current) return;
    prevDataStringRef.current = dataString;
    const currentAnswerMapMap: any = {};
    for (const activityTemplateMap of activityTemplateTypeList) {
      if (answerType === activityTemplateMap.type) {
        currentAnswerMapMap[activityTemplateMap.type] = data.answerMap;
      } else {
        currentAnswerMapMap[activityTemplateMap.type] =
          constructActivityAnswerMap(
            activityTemplateMap.type,
            JSON.parse(JSON.stringify(data))
          );
      }
    }
    setAnswerMapMap(currentAnswerMapMap);
  }, [activityTemplateTypeList, data]);

  // Ungraded (isPreview=false) the item is live, so the learner's picks must stick even when the
  // caller does not own answer state (the default changeAnswer is a no-op) — otherwise every input
  // silently ignores them. Templates mutate the map in place, hence the top-level copy.
  const handleChangeAnswer = (answerMap: any) => {
    if (!isPreview && selectedType) {
      setAnswerMapMap((prev: any) => ({
        ...prev,
        [selectedType]: { ...answerMap },
      }));
    }
    changeAnswer(answerMap);
  };

  const RenderSelectedActivityContent = () => {
    if (!selectedType) return null;

    const commonProps = {
      answerMap: answerMapMap[selectedType],
      data,
      canAnswerQuestion: () => true,
      changeAnswer: handleChangeAnswer,
      isPreview,
      showCorrectAnswer,
      isFullScreen,
    };

    let isEmpty = false;
    if (answerMapMap[selectedType] && answerMapMap[selectedType].isEmpty) {
      isEmpty = answerMapMap[selectedType].isEmpty;
    }

    // Dual-read: an activity_data doc is valid for rendering if it has either
    // the type-prefixed keys (legacy multi-template shape) or the flat keys
    // (post-migration single-template shape).
    const hasBody = readActivityDataField(data, selectedType, "bodyMap") != null;
    const hasMaterial = readActivityDataField(data, selectedType, "materialMap") != null;

    switch (selectedType) {
      case "ORDERING":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <OrderingActivityContent {...commonProps} />
          </>
        ) : null;

      case "DROPDOWN":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <DropdownActivityContent {...commonProps} />
          </>
        ) : null;

      case "MCSA":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <MCSAActivityContent {...commonProps} />
          </>
        ) : null;

      case "MCMA":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <MCMAActivityContent {...commonProps} />
          </>
        ) : null;

      case "MATCHING":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <MatchingActivityContent {...commonProps} />
          </>
        ) : null;

      case "GROUPING":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <GroupingActivityContent {...commonProps} />
          </>
        ) : null;

      case "FILL_IN_THE_BLANKS":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <FillInTheBlanksActivityContent {...commonProps} />
          </>
        ) : null;

      case "OPEN_ENDED":
        return hasBody ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <OpenEndedActivityContent
              {...commonProps}
              showMaterialContent={showMaterialContent}
            />
          </>
        ) : null;

      case "TRUE_FALSE":
        return hasBody && hasMaterial ? (
          <>
            {isEmpty && <ActivityEmptyContent />}
            <TrueFalseActivityContent {...commonProps} />
          </>
        ) : null;

      default:
        return null;
    }
  };

  if (!data) return null;

  return (
    <div>
      {showType && optionList.length > 0 ? (
        <>
          <div className="am-mb2">
            {showDescription ? (
              <div className="am-my2">
                <p className="am-prev-label">
                  {i18n.t("activity_template")}
                </p>
              </div>
            ) : null}

            <SelectionBox
              optionList={optionList}
              selectedId={selectedType}
              handleSelectOnClick={(itemId) => {
                setSelectedType(itemId);
              }}
            />
          </div>
          <DividerLine />
        </>
      ) : null}

      <div className="am-prev-main">
        {passageBodyMap && Object.keys(passageBodyMap).length > 0 ? (
          <div className="am-mb3">
            <ActivityBodyContent
              templateType="PASSAGE"
              bodyMap={passageBodyMap}
            />
            {passageReference &&
            (passageReference.title || passageReference.author) ? (
              <PassageReferenceBlock reference={passageReference} />
            ) : null}
            <DividerLine />
          </div>
        ) : null}
        {RenderSelectedActivityContent()}
      </div>

      {selectedType && showSolution ? (
        <div className="am-mb2">
          <ActivitySolutionContent
            activityTemplateType={selectedType}
            data={data}
          />
        </div>
      ) : null}

      {selectedType && showEvaluationRubric ? (
        <div className="am-mb2">
          <ActivityEvaluationRubricContent
            activityTemplateType={selectedType}
            data={data}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ActivityPreviewByData;
